import { ReportSummaryItemApi } from "../../api/Reports";
import {
  FlagCountResponse,
  GroupCount,
  TopicCount,
  ItemTypes,
  RatingTypes,
} from "../../api/Items";
import { useLocation, useNavigate } from "react-router-dom";
import { BlueprintEx2 } from "../../api/Blueprints";
import SidebarSummary, { SummaryFilterTypeEnum } from "./SidebarSummary";
import { PersonInfoList } from "./SidebarPersonInfo";
import SidebarBehaviorsByType from "./SidebarBehaviorsByType";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import Notes from "./Notes";
import { ReportDetailTypes } from "../utils";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { useReportDetail } from "../ReportDetailProvider";
import ViewReportLogsButton from "../../shared-components/ViewReportLogsButton";
import EuRegionBadge from "../../shared-components/EuRegionBadge";
import {
  classNames,
  getColorFromCheckType,
  LONG_TEXT_WRAP_CLASSES,
} from "../../utils";
import ConfirmedProfiles from "../../profiles/ConfirmedProfiles";
import SidebarAuditQAPosts from "./SidebarAuditQAPosts";

type ReportSidebarProps = {
  errorBlueprint: boolean;
  report: ReportSummaryItemApi;
  isReadOnly: boolean;
  blueprint?: BlueprintEx2;
  flagCount?: FlagCountResponse;
  keywordCount?: GroupCount;
  topicCount?: TopicCount;
  reportDetailType: ReportDetailTypes;
  applyFilterByType: (
    type: SummaryFilterTypeEnum,
    event: React.ChangeEvent<HTMLInputElement> | null,
    source: string
  ) => void;
  subStep: string;
  isFilterEmpty: boolean;
  dateFilter: { fromDate: string; toDate: string };
  summaryOnly?: boolean;
  hideNotes?: boolean;
  keywordFilter?: string[];
  topicFilter?: string[];
};
const item_types_filterable = [
  ItemTypes.SOCIAL_MEDIA_POST,
  ItemTypes.WEB_CONTENT,
];

const ReportSidebar = ({
  errorBlueprint,
  report,
  isReadOnly,
  blueprint,
  reportDetailType,
  flagCount,
  keywordCount,
  topicCount,
  applyFilterByType,
  subStep,
  isFilterEmpty,
  dateFilter,
  summaryOnly,
  hideNotes,
  keywordFilter = [],
  topicFilter = [],
}: ReportSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAddNotes } = usePermissionValidator();
  const checkTypeColor = getColorFromCheckType(blueprint?.color ?? "");
  const {
    isLoadingProfiles,
    isLoadingBlueprint,
    profileList,
    filteredCountsReady,
  } = useReportDetail();

  if (isLoadingProfiles) {
    return <LoadingSpinner className="relative" />;
  }

  let emails: string[] = [];

  const showReportLog = async () => {
    navigate(`/reports/${report.report_uuid}/logs`, {
      state: {
        workflowRunUuid: report.workflow_run_uuid,
        isReadOnly,
        fromPath: `${location.pathname}${location.search}`,
      },
    });
  };

  if (profileList)
    for (let profile of profileList) {
      emails.concat(profile.item.content.emails);
    }
  return (
    <div className="flex flex-col items-stretch min-h-full border rounded-lg pb-4">
      <div className="flex flex-col flex-start">
        {(reportDetailType === ReportDetailTypes.PROFILE_CONFIRM ||
          reportDetailType === ReportDetailTypes.SCRAPE ||
          reportDetailType === ReportDetailTypes.WEB_CONTENT) && (
          <>
            <div className="bg-gray-50 rounded-md h-auto border-slate-900 p-4 min-w-0">
              {isLoadingBlueprint ? (
                <LoadingSpinner />
              ) : (
                <div className="mb-2 min-w-0">
                  <span className="mr-1">Check Type:</span>
                  <span
                    className={classNames(
                      `font-semibold`,
                      checkTypeColor,
                      LONG_TEXT_WRAP_CLASSES
                    )}
                  >
                    {report.blueprint_name}
                  </span>
                  <br />
                  <span className="mr-1">Behavior Kit:</span>
                  <span className={classNames(`font-semibold`)}>
                    {report?.behavior_kit_name ?? "General"}
                  </span>
                </div>
              )}
              <div
                className={classNames(
                  "text-xl leading-7 font-bold mb-2",
                  LONG_TEXT_WRAP_CLASSES
                )}
              >
                {report.full_name}
                <EuRegionBadge
                  personUuid={report.person_uuid}
                  className="ml-2"
                />
              </div>
              <div className="mb-2 min-w-0">
                <span className="mr-1">Company:</span>
                <span
                  className={classNames(
                    "font-semibold",
                    LONG_TEXT_WRAP_CLASSES
                  )}
                >
                  {report.parent_company_name ?? report.company_name ?? "N/A"}
                </span>
              </div>
              <div className="mb-2 min-w-0">
                <span className="mr-1">Report ID:</span>
                <span
                  className={classNames(
                    "font-semibold",
                    LONG_TEXT_WRAP_CLASSES
                  )}
                >
                  {report.report_uuid}
                </span>
              </div>
              <ViewReportLogsButton
                onClick={showReportLog}
                canAddNotes={canAddNotes}
              />
            </div>
            <div className="p-4">
              <div className="text-lg leading-5 font-semibold mb-4">
                Person Info:
              </div>
              <PersonInfoList report={report} />
            </div>
          </>
        )}
        {reportDetailType === ReportDetailTypes.PROFILE_QA && (
          <SidebarAuditQAPosts
            report={report}
            showReportLog={showReportLog}
            applyFilterByType={applyFilterByType}
            errorBlueprint={errorBlueprint}
            checkTypeColor={checkTypeColor}
            reportDetailType={reportDetailType}
            subStep={subStep}
            isFilterEmpty={isFilterEmpty}
            dateFilter={dateFilter}
          />
        )}
        {(reportDetailType === ReportDetailTypes.SUMMARY ||
          reportDetailType === ReportDetailTypes.POST_EDIT) && (
          <SidebarSummary
            report={report}
            showReportLog={showReportLog}
            applyFilterByType={applyFilterByType}
            errorBlueprint={errorBlueprint}
            checkTypeColor={checkTypeColor}
            hideItemTypeFilter={
              reportDetailType === ReportDetailTypes.POST_EDIT
            }
            dateFilter={dateFilter}
            summaryOnly={summaryOnly}
            lookbackMonths={blueprint?.lookback_months}
            keywordFilter={keywordFilter}
            topicFilter={topicFilter}
            filtersActive={!isFilterEmpty && filteredCountsReady !== false}
            filteredFlagCount={flagCount}
            filteredKeywordCount={keywordCount}
            filteredTopicCount={topicCount}
          />
        )}
        {(reportDetailType === ReportDetailTypes.MANUAL_RATE ||
          reportDetailType === ReportDetailTypes.SYSTEM) && (
          <>
            <div className="bg-gray-50 rounded-md h-auto border-slate-900 p-4 min-w-0">
              <div className="text-center mb-2 text-sm leading-5 text-gray-600 min-w-0">
                {isLoadingBlueprint ? (
                  <LoadingSpinner />
                ) : (
                  <div className="mb-2 text-xl font-semibold">
                    <span className="mr-1">Check Type:</span>
                    <span className={classNames(`underline`, checkTypeColor)}>
                      {report.blueprint_name}
                    </span>
                    <br />
                    <span className="mr-1">Behavior Kit:</span>
                    <span className={classNames(`semibold`)}>
                      {report?.behavior_kit_name ?? "General"}
                    </span>
                  </div>
                )}
                <span className="mr-1">Report ID:</span>
                <span className={LONG_TEXT_WRAP_CLASSES}>
                  {report.report_uuid}
                </span>
              </div>
              <ViewReportLogsButton
                onClick={showReportLog}
                canAddNotes={canAddNotes}
              />
            </div>
            <div className="mt-6">
              <h6 className="text-lg leading-6 font-semibold pl-4">
                System Rated Post Summary:
              </h6>
              <div className="p-4">
                {blueprint && (
                  <SidebarBehaviorsByType
                    report={report}
                    itemTypeNames={item_types_filterable}
                    ratingType={RatingTypes.System}
                    behaviors={blueprint.behaviors}
                  />
                )}
              </div>
            </div>
          </>
        )}

        {!summaryOnly && !hideNotes && (
          <>
            <Notes isReadOnly={isReadOnly} report={report} />
            {reportDetailType === ReportDetailTypes.WEB_CONTENT && (
              <ConfirmedProfiles
                profileList={profileList ?? []}
                isReadOnly={true}
                isSidebar={true}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReportSidebar;
